<template>
    <div>
        <div class="scene-loading" v-if="isLoading">
            <Loading />
        </div>

        <div
            :id="`confirm-face-${face.face_id}`"
            style="width: 100%; height: 100%"
        ></div>
    </div>
</template>
<script>
import { markRaw } from "vue";
import * as THREE from "three";
import { GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader.js";
import { Text } from "troika-three-text";
import { getAngleNumber } from "../../utils/calcAngle";
import modelToMesh from "../../utils/modelToMesh";
import getPosInfo from "../../utils/getPosInfo";
export default {
    name: "ThreeSceneEngraving",
    props: {
        face: {
            type: Object,
            default: null,
        },
    },
    data() {
        return {
            isLoading: false,
            isComponentReady: false, // Флаг готовности компонента
            canvas: null,
            renderer: null,
            scene: null,
            camera: null,
            gltfLoader: null,
            directionalLight: null,
            sizes: null,
            textureLoader: null,
            faceElementsGroup: null,
            faceTextsGroup: null,
            mainElement: null,
            sceneMeshes: [],
        };
    },
    methods: {
        async createMockup() {
            let captureRenderer = null;
            try {
                console.log(
                    `Creating mockup for face ${this.face?.face_id} (${
                        this.face?.faceName || "unnamed"
                    })`
                );

                if (!this.renderer || !this.scene || !this.camera) {
                    console.warn(
                        `Scene not ready for face ${this.face?.face_id}:`,
                        {
                            renderer: !!this.renderer,
                            scene: !!this.scene,
                            camera: !!this.camera,
                        }
                    );
                    return;
                }

                const hasMainElement = !!this.mainElement;
                const hasElements =
                    this.faceElementsGroup &&
                    this.faceElementsGroup.children.length > 0;
                const hasTexts =
                    this.faceTextsGroup &&
                    this.faceTextsGroup.children.length > 0;
                const hasContent = hasMainElement || hasElements || hasTexts;

                console.log(`Face ${this.face?.face_id} content check:`, {
                    hasMainElement,
                    hasElements,
                    hasTexts,
                    elementsCount: hasElements
                        ? this.faceElementsGroup.children.length
                        : 0,
                    textsCount: hasTexts
                        ? this.faceTextsGroup.children.length
                        : 0,
                });

                if (!hasContent) {
                    console.warn(
                        `No content to render for face ${this.face?.face_id}`
                    );
                    return;
                }

                const captureSize = 1000;

                const captureCamera = this.camera.clone();
                captureCamera.aspect = 1;
                captureCamera.updateProjectionMatrix();

                if (this.mainElement) {
                    const boundingBox = new THREE.Box3();
                    boundingBox.setFromObject(this.mainElement);

                    const size = new THREE.Vector3();
                    boundingBox.getSize(size);

                    const fov = captureCamera.fov * (Math.PI / 180);
                    const fovh =
                        2 * Math.atan(Math.tan(fov / 2) * captureCamera.aspect);
                    const dx =
                        size.z / 2 + Math.abs(size.x / 2 / Math.tan(fovh / 2));
                    const dy =
                        size.z / 2 + Math.abs(size.y / 2 / Math.tan(fov / 2));
                    const cameraZ = Math.max(dx, dy);

                    captureCamera.position.set(0, 0, cameraZ);

                    const minZ = boundingBox.min.z;
                    const cameraToFarEdge =
                        minZ < 0 ? -minZ + cameraZ : cameraZ - minZ;
                    captureCamera.far = cameraToFarEdge * 3;
                    captureCamera.updateProjectionMatrix();
                } else if (
                    this.faceElementsGroup &&
                    this.faceElementsGroup.children.length > 0
                ) {
                    const bbox = new THREE.Box3();
                    this.faceElementsGroup.children.forEach((child) => {
                        bbox.expandByObject(child);
                    });
                    if (
                        this.faceTextsGroup &&
                        this.faceTextsGroup.children.length > 0
                    ) {
                        this.faceTextsGroup.children.forEach((child) => {
                            bbox.expandByObject(child);
                        });
                    }
                    const size = new THREE.Vector3();
                    bbox.getSize(size);
                    const center = new THREE.Vector3();
                    bbox.getCenter(center);

                    const distance = Math.max(size.x, size.y, size.z) * 1.5;
                    captureCamera.position.set(
                        center.x,
                        center.y,
                        center.z + distance
                    );
                    captureCamera.lookAt(center);
                    captureCamera.updateProjectionMatrix();
                } else {
                    captureCamera.position.set(0, 0, 2);
                    captureCamera.updateProjectionMatrix();
                }

                captureRenderer = new THREE.WebGLRenderer({
                    antialias: true,
                    alpha: true,
                    preserveDrawingBuffer: true,
                });
                captureRenderer.setPixelRatio(1);
                captureRenderer.setSize(captureSize, captureSize);

                for (let i = 0; i < 3; i++) {
                    captureRenderer.render(this.scene, captureCamera);
                    await new Promise((resolve) => setTimeout(resolve, 50));
                }

                await new Promise((resolve) => setTimeout(resolve, 200));

                const image = captureRenderer.domElement.toDataURL("image/png");

                if (!image || image === "data:,") {
                    const errorMsg = `Не удалось сгенерировать изображение для грани "${
                        this.face?.faceName || this.face?.face_id
                    }"`;
                    console.warn(errorMsg, {
                        imageLength: image ? image.length : 0,
                        imagePrefix: image ? image.substring(0, 50) : "null",
                    });

                    this.$store.dispatch(
                        "notification/add",
                        {
                            type: "warning",
                            message: errorMsg,
                        },
                        { root: true }
                    );

                    return;
                }

                if (!image.startsWith("data:image")) {
                    const errorMsg = `Сгенерированное изображение для грани "${
                        this.face?.faceName || this.face?.face_id
                    }" имеет неверный формат`;
                    console.warn(errorMsg, {
                        imagePrefix: image.substring(0, 50),
                    });

                    this.$store.dispatch(
                        "notification/add",
                        {
                            type: "warning",
                            message: errorMsg,
                        },
                        { root: true }
                    );

                    return;
                }

                this.$store.dispatch("clientOrder/setFaceFinalImage", {
                    faceId: this.face.face_id,
                    finalImage: image,
                });

                await this.$nextTick();
                await new Promise((resolve) => setTimeout(resolve, 100));

                const faceInStore = this.$store.state.clientOrder.faces.find(
                    (f) => f.face_id === this.face.face_id
                );

                if (faceInStore && faceInStore.finalImage) {
                    const isImageValid =
                        faceInStore.finalImage.startsWith("data:image") &&
                        faceInStore.finalImage.length > 100;

                    if (isImageValid) {
                        console.log(
                            `Mockup generated and saved successfully for face ${
                                this.face.face_id
                            } (${
                                this.face?.faceName || "unnamed"
                            }), image size: ${(image.length / 1024).toFixed(
                                2
                            )} KB`
                        );
                    } else {
                        console.warn(
                            `Mockup saved but image seems invalid for face ${this.face.face_id}:`,
                            {
                                finalImageLength: faceInStore.finalImage.length,
                                finalImagePrefix:
                                    faceInStore.finalImage.substring(0, 50),
                            }
                        );
                    }
                } else {
                    console.error(
                        `Mockup generated but NOT saved to store for face ${this.face.face_id}:`,
                        {
                            faceInStore: !!faceInStore,
                            hasFinalImage: faceInStore
                                ? !!faceInStore.finalImage
                                : false,
                            faceId: this.face.face_id,
                            allFaceIds: this.$store.state.clientOrder.faces.map(
                                (f) => f.face_id
                            ),
                        }
                    );

                    this.$store.dispatch("clientOrder/setFaceFinalImage", {
                        faceId: this.face.face_id,
                        finalImage: image,
                    });

                    await this.$nextTick();
                    await new Promise((resolve) => setTimeout(resolve, 100));

                    const faceInStoreAfterRetry =
                        this.$store.state.clientOrder.faces.find(
                            (f) => f.face_id === this.face.face_id
                        );

                    if (
                        faceInStoreAfterRetry &&
                        faceInStoreAfterRetry.finalImage
                    ) {
                        console.log(
                            `Mockup saved successfully on retry for face ${this.face.face_id}`
                        );
                    } else {
                        console.error(
                            `Failed to save mockup to store after retry for face ${this.face.face_id}`
                        );
                        throw new Error(
                            `Failed to save mockup to store for face ${this.face.face_id}`
                        );
                    }
                }
            } catch (error) {
                console.error(
                    `Error creating mockup for face ${this.face?.face_id}:`,
                    error
                );

                this.$store.dispatch(
                    "notification/add",
                    {
                        type: "error",
                        message: `Ошибка при генерации макета грани "${
                            this.face?.faceName || this.face?.face_id
                        }": ${error.message || "Неизвестная ошибка"}`,
                    },
                    { root: true }
                );

                throw error;
            } finally {
                if (captureRenderer) {
                    captureRenderer.dispose();
                    if (
                        typeof captureRenderer.forceContextLoss === "function"
                    ) {
                        captureRenderer.forceContextLoss();
                    }
                }
            }
        },
        getMaterialTexture: async function (
            path = null,
            model = false,
            land = false
        ) {
            let material = null;
            if (model) {
                material = new THREE.MeshPhysicalMaterial({
                    clearcoat: 0.5,
                    flatShading: true,
                    roughness: 0.2,
                    metalness: 0.7,
                    depthTest: true,
                    depthWrite: true,
                    reflectivity: 0.9,
                });
            } else {
                material = new THREE.MeshStandardMaterial({
                    side: THREE.DoubleSide,
                });
            }
            if (path) {
                // const texture = await this.textureLoader.load(path);
                const [texture] = await Promise.all([
                    this.textureLoader.loadAsync(path),
                ]);
                if (land) {
                    texture.repeat.set(8, 8);
                    texture.wrapS = THREE.RepeatWrapping;
                    texture.wrapT = THREE.RepeatWrapping;
                }
                material.map = texture;
            } else if (!model) {
                material.color = new THREE.Color("#aeaece");
            }
            return material;
        },
        getGLTF: async function (path) {
            const [gltf] = await Promise.all([this.gltfLoader.loadAsync(path)]);
            return gltf;
        },
        async addElement(el) {
            try {
                let gltf = await this.getGLTF(el.model3d);
                if (!gltf) {
                    console.warn(
                        `ThreeSceneEngravingConfirm: GLTF не загружен для face ${this.face?.face_id}, element ${el.element_id}`
                    );
                    return;
                }
                if (
                    parseInt(el.individual_size_material) == 0 &&
                    el.material &&
                    el.material.image
                ) {
                    const materialImage =
                        el.material.imageOriginal || el.material.image;
                    const material = await this.getMaterialTexture(
                        materialImage,
                        true,
                        true
                    );
                    gltf.scene.traverse(function (node) {
                        if (node.isMesh) {
                            if (!node.name.includes("_notexture")) {
                                node.material = material;
                            }
                        }
                    });
                    if (el.element_group_slug == "cvetniki-nadgrobiya") {
                        let cvetnikMaterial = new THREE.MeshStandardMaterial({
                            color: 0xcecece,
                        });
                        if (
                            parseInt(this.settings.filler_cvetnik_texture) != 1
                        ) {
                            let cvetnikMaterialTexture =
                                "/textures/cvetnik/grass.jpg";
                            if (
                                parseInt(
                                    this.settings.filler_cvetnik_texture
                                ) == 2
                            ) {
                                cvetnikMaterialTexture =
                                    this.settingImages.filler_cvetnik_texture;
                            }
                            cvetnikMaterial = await this.getMaterialTexture(
                                cvetnikMaterialTexture,
                                false,
                                true
                            );
                        }
                        gltf.scene.traverse(function (node) {
                            if (node.isMesh) {
                                if (node.name.includes("_grass")) {
                                    node.material = cvetnikMaterial;
                                }
                            }
                        });
                    }
                }

                let scaleCoef = { x: 1, y: 1, z: 1 };
                let model = null;

                if (el.size) {
                    if (el.size.model3d) {
                        const sizeGltf = await this.getGLTF(el.size.model3d);
                        if (sizeGltf) {
                            if (
                                parseInt(el.individual_size_material) == 0 &&
                                el.material &&
                                el.material.image
                            ) {
                                const material = await this.getMaterialTexture(
                                    el.material.image,
                                    true,
                                    true
                                );
                                sizeGltf.scene.traverse(function (node) {
                                    if (node.isMesh) {
                                        if (!node.name.includes("_notexture")) {
                                            node.material = material;
                                        }
                                    }
                                });
                            }
                            model = await modelToMesh(sizeGltf.scene);
                        }
                    } else {
                        if (
                            el.size.width &&
                            el.size.height &&
                            el.size.length &&
                            el.base_size &&
                            el.base_size.width &&
                            el.base_size.height &&
                            el.base_size.length
                        ) {
                            scaleCoef = {
                                x:
                                    parseFloat(el.size.width) /
                                    parseFloat(el.base_size.width),
                                y:
                                    parseFloat(el.size.height) /
                                    parseFloat(el.base_size.height),
                                z:
                                    parseFloat(el.size.length) /
                                    parseFloat(el.base_size.length),
                            };

                            model = await modelToMesh(gltf.scene);
                            model.geometry.scale(
                                scaleCoef.x,
                                scaleCoef.y,
                                scaleCoef.z
                            );

                            model.updateMatrix();
                        }
                    }
                }

                // Fallback: если model не был создан, создаем из базовой модели gltf
                if (!model && gltf) {
                    model = await modelToMesh(gltf.scene);
                }

                // Проверяем, что model существует перед применением трансформаций
                if (!model) {
                    console.warn(
                        `ThreeSceneEngravingConfirm: не удалось создать model для face ${this.face?.face_id}`
                    );
                    return;
                }

                if (parseInt(this.face.rotate_x)) {
                    model.rotation["x"] = -getAngleNumber(
                        parseInt(this.face.rotate_x)
                    );
                }

                if (parseInt(this.face.rotate_y)) {
                    model.rotation["y"] = -getAngleNumber(
                        parseInt(this.face.rotate_y)
                    );
                }

                if (parseInt(this.face.rotate_z)) {
                    model.rotation["z"] = getAngleNumber(
                        parseInt(this.face.rotate_z)
                    );
                }

                // three.js‑объекты помечаем как raw, чтобы Vue не оборачивал их в Proxy
                this.mainElement = markRaw(model);
                this.scene.add(this.mainElement);
            } catch (error) {
                console.error(
                    `ThreeSceneEngravingConfirm: ошибка при добавлении элемента для face ${this.face?.face_id}:`,
                    error
                );
                // Не прерываем выполнение, продолжаем генерацию даже без mainElement
                // если есть элементы гравировки или тексты
            }
        },
        async addEngravingElement(el) {
            const elImage = el.imageOriginal || el.image;
            if (elImage) {
                var loader = new THREE.TextureLoader();
                const texture = loader.load(elImage);
                var material = new THREE.MeshToonMaterial({
                    map: texture,
                    alphaMap: texture,
                    alphaTest: 0.01,
                    transparent: true,
                    //   depthWrite: false,
                    //   depthTest: false,
                    side: THREE.DoubleSide,
                });
                const widthCm = Number(el?.width ?? el?.size?.width);
                const heightCm = Number(el?.height ?? el?.size?.height);
                if (!Number.isFinite(widthCm) || !Number.isFinite(heightCm) || widthCm <= 0 || heightCm <= 0) {
                    return;
                }
                var geometry = new THREE.PlaneGeometry(widthCm / 100, heightCm / 100);
                var mesh = markRaw(new THREE.Mesh(geometry, material));

                if (el.mirrored) {
                    mesh.rotation.y = Math.PI;
                }

                mesh.rotation.z = getAngleNumber(el.angle);

                if (this.mainElement) {
                    const mainElementBbox = getPosInfo(this.mainElement);
                    const meshBbox = getPosInfo(mesh);

                    mesh.position.z = mainElementBbox.bbox.max.z + parseFloat(el.layer);

                    const offsetTop = parseFloat(el.offsetTop) / 100;
                    const offsetLeft = parseFloat(el.offsetLeft) / 100;

                    mesh.position.y =
                        mainElementBbox.bbox.max.y -
                        meshBbox.ySize / 2 -
                        offsetTop;

                    mesh.position.x =
                        mainElementBbox.bbox.min.x +
                        meshBbox.xSize / 2 +
                        offsetLeft;
                }

                this.faceElementsGroup.add(mesh);
            }
        },
        async addText(text) {
            try {
                const myText = markRaw(new Text());
                this.faceTextsGroup.add(myText);

                myText.text = text.text;
                myText.fontSize = parseFloat(text.size.height) / 100;
                myText.font = text.font.file;
                myText.lineHeight = 1;
                myText.outlineWidth = 0;

                if (text.align && text.align.id) {
                    myText.textAlign = text.align.id;
                }
                myText.color = 0xffffff;

                if (text.angle) {
                    myText.rotation.z = getAngleNumber(text.angle);
                }

                if (this.mainElement) {
                    const mainElementBbox = getPosInfo(this.mainElement);
                    myText.maxWidth = mainElementBbox.xSize;

                    myText.position.z = mainElementBbox.bbox.max.z + text.layer;

                    const offsetTop = text.offsetTop / 100;
                    const offsetLeft = text.offsetLeft / 100;

                    myText.position.x = mainElementBbox.bbox.min.x + offsetLeft;
                    myText.position.y = mainElementBbox.bbox.max.y - offsetTop;
                } else {
                    // Если mainElement отсутствует, используем дефолтные позиции
                    myText.position.set(0, 0, 0);
                }

                // Синхронизация текста
                myText.sync();

                // Небольшая задержка для завершения синхронизации
                await new Promise((resolve) => setTimeout(resolve, 100));
            } catch (error) {
                console.error(
                    `ThreeSceneEngravingConfirm: ошибка при добавлении текста для face ${this.face?.face_id}:`,
                    error
                );
                // Продолжаем выполнение даже при ошибке добавления текста
            }
        },

        async fitCameraToObject() {
            // Проверяем наличие mainElement
            if (!this.mainElement) {
                console.warn(
                    `fitCameraToObject: mainElement not available for face ${this.face?.face_id}`
                );
                return;
            }

            const boundingBox = new THREE.Box3();

            const object = this.mainElement;

            boundingBox.setFromObject(object);

            var middle = new THREE.Vector3();
            var size = new THREE.Vector3();
            boundingBox.getSize(size);
            const fov = this.camera.fov * (Math.PI / 180);
            const fovh = 2 * Math.atan(Math.tan(fov / 2) * this.camera.aspect);
            let dx = size.z / 2 + Math.abs(size.x / 2 / Math.tan(fovh / 2));
            let dy = size.z / 2 + Math.abs(size.y / 2 / Math.tan(fov / 2));
            let cameraZ = Math.max(dx, dy);

            this.camera.position.set(0, 0, cameraZ);

            const minZ = boundingBox.min.z;
            const cameraToFarEdge = minZ < 0 ? -minZ + cameraZ : cameraZ - minZ;

            this.camera.far = cameraToFarEdge * 3;
            this.camera.updateProjectionMatrix();
        },

        init: async function () {
            this.canvas = document.getElementById(
                `confirm-face-${this.face.face_id}`
            );
            this.gltfLoader = markRaw(new GLTFLoader());
            this.textureLoader = markRaw(new THREE.TextureLoader());
            this.faceElementsGroup = markRaw(new THREE.Group());
            this.faceTextsGroup = markRaw(new THREE.Group());
            this.scene = markRaw(new THREE.Scene());
            this.scene.add(this.faceElementsGroup, this.faceTextsGroup);
            this.scene.background = null;
            // чуть мягче и теплее подсветка для естественного вида
            this.directionalLight = markRaw(
                new THREE.DirectionalLight("#ffffff", 2.2)
            );
            this.directionalLight.position.set(0, 2, 1);
            this.scene.add(this.directionalLight);
            this.sizes = {
                width: this.canvas?.clientWidth,
                height: this.canvas?.clientHeight,
            };
            this.camera = markRaw(
                new THREE.PerspectiveCamera(
                    65,
                    this.sizes.width / this.sizes.height,
                    0.1,
                    1000
                )
            );
            this.camera.position.set(0, 0, 1);
            this.scene.add(this.camera);
            this.renderer = markRaw(
                new THREE.WebGLRenderer({
                    antialias: true,
                    alpha: true,
                    preserveDrawingBuffer: true,
                })
            );
            this.renderer.setSize(this.sizes.width, this.sizes.height);
            this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
            this.canvas.appendChild(this.renderer.domElement);
        },
        animate: function () {
            requestAnimationFrame(this.animate);
            this.renderer.render(this.scene, this.camera);
        },

        isReady() {
            return (
                this.isComponentReady &&
                this.renderer &&
                this.scene &&
                this.camera
            );
        },

        async ensureReady() {
            // Ожидание готовности компонента
            let attempts = 0;
            const maxAttempts = 50; // 5 секунд максимум

            while (!this.isReady() && attempts < maxAttempts) {
                await new Promise((resolve) => setTimeout(resolve, 100));
                attempts++;
            }

            return this.isReady();
        },
    },
    async mounted() {
        this.isLoading = true;
        this.isComponentReady = false;

        try {
            // 1. Инициализация базовых компонентов
            await this.init();
            this.animate();

            // 2. Добавление основного элемента
            if (this.face?.element?.model3d) {
                await this.addElement(this.face.element);
            } else {
                console.warn(
                    `ThreeSceneEngravingConfirm: face.element.model3d не найден для face ${this.face?.face_id}`
                );
            }

            // 3. Добавление элементов гравировки
            if (this.face?.elements?.length) {
                for (const el of this.face.elements) {
                    await this.addEngravingElement(el);
                }
            }

            // 4. Добавление текстов
            if (this.face?.texts?.length) {
                for (const text of this.face.texts) {
                    await this.addText(text);
                }

                // Ожидание синхронизации всех текстов (troika-three-text требует времени)
                await new Promise((resolve) => setTimeout(resolve, 300));

                // Принудительная синхронизация всех текстов
                if (
                    this.faceTextsGroup &&
                    this.faceTextsGroup.children.length > 0
                ) {
                    for (const textObj of this.faceTextsGroup.children) {
                        if (textObj && typeof textObj.sync === "function") {
                            textObj.sync();
                        }
                    }
                    // Еще одна задержка после синхронизации
                    await new Promise((resolve) => setTimeout(resolve, 200));
                }
            }

            // 5. Ожидание завершения всех асинхронных операций
            await this.$nextTick();

            // 6. Позиционирование камеры
            if (this.mainElement) {
                await this.fitCameraToObject();
            } else {
                // Дефолтная позиция, если mainElement отсутствует
                this.camera.position.set(0, 0, 2);
                this.camera.updateProjectionMatrix();
            }

            // 7. Дополнительная задержка для завершения рендеринга
            await new Promise((resolve) => setTimeout(resolve, 300));

            // 8. Проверка готовности перед генерацией
            if (this.renderer && this.scene && this.camera) {
                // Несколько принудительных рендеров для инициализации
                for (let i = 0; i < 3; i++) {
                    this.renderer.render(this.scene, this.camera);
                    await new Promise((resolve) => setTimeout(resolve, 100));
                }

                // Генерация макета с повторной попыткой при неудаче
                let attempts = 0;
                const maxAttempts = 3;
                let success = false;

                while (attempts < maxAttempts && !success) {
                    try {
                        await this.createMockup();
                        // Проверяем, что finalImage установлен
                        await this.$nextTick();
                        const faceInStore =
                            this.$store.state.clientOrder.faces.find(
                                (f) => f.face_id === this.face.face_id
                            );
                        if (faceInStore && faceInStore.finalImage) {
                            success = true;
                            console.log(
                                `Mockup generation succeeded on attempt ${
                                    attempts + 1
                                } for face ${this.face.face_id}`
                            );
                        } else {
                            attempts++;
                            if (attempts < maxAttempts) {
                                console.warn(
                                    `Mockup not saved, retrying... (attempt ${
                                        attempts + 1
                                    }/${maxAttempts})`
                                );
                                await new Promise((resolve) =>
                                    setTimeout(resolve, 500)
                                );
                            }
                        }
                    } catch (error) {
                        attempts++;
                        if (attempts < maxAttempts) {
                            console.warn(
                                `Mockup generation failed, retrying... (attempt ${
                                    attempts + 1
                                }/${maxAttempts}):`,
                                error
                            );
                            await new Promise((resolve) =>
                                setTimeout(resolve, 500)
                            );
                        } else {
                            throw error;
                        }
                    }
                }

                if (!success) {
                    console.error(
                        `Failed to generate mockup after ${maxAttempts} attempts for face ${this.face.face_id}`
                    );
                }
            } else {
                console.warn(
                    `ThreeSceneEngravingConfirm: сцена не готова для face ${this.face?.face_id}`
                );
            }

            this.isComponentReady = true;
            this.isLoading = false;
        } catch (error) {
            console.error(
                `ThreeSceneEngravingConfirm mounted error for face ${this.face?.face_id}:`,
                error
            );
            this.isLoading = false;
            this.isComponentReady = false;

            // Уведомление пользователю
            this.$store.dispatch(
                "notification/add",
                {
                    type: "error",
                    message: `Ошибка при инициализации макета грани "${
                        this.face?.faceName || this.face?.face_id
                    }". Попробуйте обновить страницу.`,
                },
                { root: true }
            );
        }
    },
    computed: {
        settings() {
            return this.$store.state.settings.settings;
        },
        settingImages() {
            return this.$store.state.settings.settingImages;
        },
    },
};
</script>
<style scoped>
.scene-loading {
    background-color: rgba(0, 0, 0, 0.1);
    position: fixed;
    left: 0;
    right: 0;
    top: 0;
    bottom: 0;
    display: grid;
    place-items: center;
}
</style>
